import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import type { SetupState } from "../../shared/types";
import { useAuth } from "../auth/AuthContext";
import { api } from "../lib/api";

/** Setup's four steps, in order, each with the screen that finishes it. */
export const SETUP_STEPS = [
  { key: "look", label: "Your look", path: "/setup/look" },
  { key: "email", label: "Your email", path: "/setup/email" },
  { key: "address", label: "Your web address", path: "/setup/address" },
  { key: "tracking", label: "Tracking", path: "/setup/tracking" },
] as const;

export function setupCount(s: SetupState): number {
  return SETUP_STEPS.filter((x) => s[x.key]).length;
}

/** Where to pick setup up: the first step not done, or the end once all four are. */
export function nextSetupPath(s: SetupState): string {
  return SETUP_STEPS.find((x) => !s[x.key])?.path ?? "/setup/ready";
}

interface SetupContextValue {
  /** Null until the first read lands. */
  state: SetupState | null;
  /** Only an owner or admin can change settings, so only they are asked to finish setup. */
  canEdit: boolean;
  set: (s: SetupState) => void;
  refresh: () => Promise<void>;
}

const SetupContext = createContext<SetupContextValue | null>(null);

/**
 * One reading of first-run setup for the top bar's pill, Settings, Home and the setup screens. A business that has
 * never seen setup is taken there once, from Home, by someone who can do it.
 */
export function SetupProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [state, setState] = useState<SetupState | null>(null);
  const canEdit = user?.role === "owner" || user?.role === "admin";
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const sent = useRef(false);

  const refresh = useCallback(async () => {
    try {
      setState(await api.settings.setup());
    } catch {
      // The pill simply stays away until a read lands.
    }
  }, []);

  // Why a key, not the user object: read once per signed-in person and business, not on every new object.
  const who = user ? `${user.workspace_id}:${user.id}` : null;
  useEffect(() => {
    if (who) void refresh();
  }, [who, refresh]);

  useEffect(() => {
    if (sent.current || !state || state.seen || !canEdit || pathname !== "/") return;
    sent.current = true;
    navigate("/setup", { replace: true });
  }, [state, canEdit, pathname, navigate]);

  const value = useMemo(() => ({ state, canEdit, set: setState, refresh }), [state, canEdit, refresh]);
  return <SetupContext.Provider value={value}>{children}</SetupContext.Provider>;
}

// Why one object: a screen outside the provider (a test, a public page) must get the same functions every render.
const NO_SETUP: SetupContextValue = { state: null, canEdit: false, set: () => undefined, refresh: async () => undefined };

export function useSetup(): SetupContextValue {
  return useContext(SetupContext) ?? NO_SETUP;
}
